/**
 * The two ways a member is reached, and what counts as one of each.
 *
 * Ported from `legacy/frontendx/src/apps/mrm/utils/mobileNumberValidation.ts`
 * and `emailValidation.ts`. The rules are the API's own said early: the service
 * stores a mobile as `+63` and ten digits and refuses anything else, so the
 * field is held to ten digits as it is typed rather than after a round trip.
 */

/** A Philippine mobile as the API stores it: `+63` and exactly ten digits. */
export const MOBILE_DIGITS = 10;

/**
 * Practical email validation, stricter than the browser's.
 *
 * `type="email"` accepts `#@1.c`, which no mail server would. This is legacy's
 * regex: a local part that starts and ends alphanumeric, a domain of labels,
 * and a top-level domain of at least two letters.
 */
const EMAIL_FORMAT =
  /^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

export function isValidEmail(value: string | null | undefined): boolean {
  if (value == null) return false;
  const email = value.trim();
  // 254 is the address length the API takes; `..` is legal nowhere in practice
  // and the regex alone would let it through.
  if (!email || email.length > 254) return false;
  if (email.includes("..")) return false;
  return EMAIL_FORMAT.test(email);
}

/** What a keystroke in a mobile field leaves behind: digits, at most ten of them. */
export function sanitizeMobile(value: string): string {
  return value.replace(/\D/g, "").slice(0, MOBILE_DIGITS);
}

/**
 * The ten digits after the country code, from a number however it was stored.
 *
 * The field shows `+63` beside the box rather than inside it, so the value in
 * the box is the local part — and a stored number may carry `+63`, a bare `63`
 * or the domestic leading `0`, which is the same number three ways.
 */
export function localMobile(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim().replace(/[\s\-()]/g, "");
  const bare = trimmed.startsWith("+63")
    ? trimmed.slice(3)
    : trimmed.startsWith("63") && trimmed.length === MOBILE_DIGITS + 2
      ? trimmed.slice(2)
      : trimmed.startsWith("0")
        ? trimmed.slice(1)
        : trimmed;
  return bare.replace(/\D/g, "");
}

export function isValidMobile(value: string | null | undefined): boolean {
  return new RegExp(`^\\d{${MOBILE_DIGITS}}$`).test(value ?? "");
}

/** The local part put back together the way the API wants it, or null for nothing typed. */
export function toApiMobile(local: string): string | null {
  const digits = local.trim();
  return digits ? `+63${digits}` : null;
}
